import { useEffect, useRef, useState } from 'react';
import { ArrowDown, Play } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';

const VIDEO_BASE = '/demo/ingest-demo';

export function IngestDemoSection() {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);

  // Autoplay when scrolled into view (first time only). preload="none" keeps
  // the prerender's `networkidle0` window clean — the video doesn't start
  // fetching until IntersectionObserver triggers .load() + .play() in the
  // real browser.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || hasPlayed || videoFailed) return;

    const start = () => {
      const video = videoRef.current;
      if (!video) return;
      // With preload="none" we must explicitly call load() so the browser
      // walks the <source> children and picks one. Then play() begins
      // streaming. Some browsers also need this on user-gesture fallback.
      try {
        video.load();
      } catch {
        /* ignore */
      }
      video.play().catch(() => {
        // Autoplay may be blocked even though we're muted — the native
        // controls remain visible so the visitor can hit play themselves.
      });
      setHasPlayed(true);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          start();
          observer.disconnect();
        }
      },
      // 0.25 fires early enough that the video is already playing when
      // visitors finish scrolling to it.
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasPlayed, videoFailed]);

  return (
    <section
      ref={sectionRef}
      id="demo"
      className="py-20 sm:py-28 gradient-subtle"
      aria-labelledby="demo-heading"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mx-auto text-center mb-10 sm:mb-14 animate-on-scroll">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-4">
            <Play className="h-3.5 w-3.5 text-primary-500" />
            <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
              {t('ingestDemo.badge')}
            </span>
          </div>
          <h2
            id="demo-heading"
            className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance"
          >
            {t('ingestDemo.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('ingestDemo.subtitle')}
          </p>
        </div>

        {/* Video frame */}
        <div className="animate-on-scroll relative mx-auto max-w-5xl">
          <div
            className={cn(
              'relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800',
              'bg-gray-950 shadow-2xl shadow-primary-500/10',
              'aspect-[16/9]',
            )}
          >
            {videoFailed ? (
              <DemoFallback />
            ) : (
              <video
                ref={videoRef}
                className="absolute inset-0 h-full w-full"
                src={`${VIDEO_BASE}.mp4`}
                poster={`${VIDEO_BASE}-poster.jpg`}
                muted
                loop
                playsInline
                preload="none"
                controls
                onError={() => {
                  // Only flip to fallback when the browser truly has no
                  // playable source — avoids spurious errors on transient
                  // network blips while loading.
                  if (
                    videoRef.current?.networkState ===
                    HTMLMediaElement.NETWORK_NO_SOURCE
                  ) {
                    setVideoFailed(true);
                  }
                }}
                aria-label={t('ingestDemo.title')}
              />
            )}
            {/* Subtle glow */}
            <div className="absolute -inset-4 -z-10 bg-gradient-to-r from-primary-500/15 via-primary-400/10 to-primary-500/15 rounded-3xl blur-2xl opacity-60" />
          </div>

          {/* Hand-off arrow into AI Investigator */}
          <a
            href="#ai-agent"
            className="mt-8 mx-auto flex items-center justify-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors w-fit"
          >
            {t('ingestDemo.handoff')}
            <ArrowDown className="h-4 w-4 animate-bounce" />
          </a>
        </div>
      </div>
    </section>
  );
}

function DemoFallback() {
  const t = useT();
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-950 to-purple-950/40">
      <div className="text-center max-w-md px-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 mb-5">
          <Play className="h-7 w-7 text-white ml-0.5" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('ingestDemo.fallback.title')}
        </h3>
        <p className="text-sm text-gray-400 leading-relaxed">
          {t('ingestDemo.fallback.body')}
        </p>
      </div>
    </div>
  );
}
